/**
 * Running a long job in pieces, so no single frame pays for all of it.
 *
 * Viewsheds and inferred doors are the two genuinely expensive things this map
 * computes: 72 rays out of every camera on screen tested against the footprint
 * index, and a pass over every footprint in view. Moving them off the end of a
 * gesture stopped them stalling the pan, but they still arrived as one task of
 * a few hundred milliseconds — the gesture ran clean and then the map hitched
 * once, which is the same lost frames in a different place.
 *
 * This spends a budget per frame instead. Work continues on the next frame, so
 * the browser gets to draw in between and nothing is ever blocked for longer
 * than the budget.
 */

/**
 * Eight milliseconds of a sixteen millisecond frame.
 *
 * Half the frame, so there is room left for the map to draw in the same one.
 * The budget is checked between items rather than inside them, so a single
 * item that takes longer than this will still overshoot — the granularity of
 * the work is the floor on how well this can behave.
 */
const DEFAULT_BUDGET_MS = 8;

/**
 * Process `items` a few at a time, calling `step` for each.
 *
 * Returns a cancel function. Calling it stops the run where it is, which is
 * what a new gesture wants — the answer being computed is about a viewport
 * nobody is looking at any more.
 *
 * `onProgress` runs at the end of each slice and `onDone` when the last item
 * is finished, so a caller can show partial results as they arrive rather than
 * waiting for all of them.
 */
export function runSliced(items, step, {
  budgetMs = DEFAULT_BUDGET_MS, onProgress, onDone,
} = {}) {
  let index = 0;
  let handle = null;
  let cancelled = false;

  const frame = () => {
    if (cancelled) return;
    const deadline = performance.now() + budgetMs;

    while (index < items.length && performance.now() < deadline) {
      step(items[index], index);
      index += 1;
    }

    if (index >= items.length) {
      handle = null;
      onDone?.();
      return;
    }

    onProgress?.(index, items.length);
    handle = requestAnimationFrame(frame);
  };

  // Started on the next frame rather than immediately: the caller is usually
  // inside an event handler, and the point is to not spend time there.
  handle = requestAnimationFrame(frame);

  return () => {
    cancelled = true;
    if (handle !== null) cancelAnimationFrame(handle);
    handle = null;
  };
}

/** Split an array into fixed-size chunks, for work that batches better. */
export function chunk(items, size) {
  const out = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
